import {
    AdobeAnalyticsService,
    IAdobeInteraction,
    IAdobePageLoad,
    InteractionAnalyticsMap,
    PageLoadAnalyticsMap
} from "@abbvie-epi/common/models";
import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { pageLoadAnalytics } from "../configs/page-load-analytics.config";
import { analyticsInteractions } from "../configs/user-interaction-analytics.config";

import { PlatformLocation } from "@angular/common";
import { ObjectHelpers } from "../common/classes/ObjectHelpers";

@Injectable({
    providedIn: "root"
})
export class AnalyticsHelperService {
    private _pageAnalytics: PageLoadAnalyticsMap | null;
    private _interactionAnalytics: InteractionAnalyticsMap | null;

    constructor(
        private analyticsService: AdobeAnalyticsService,
        private titleService: Title,
        private platformLocation: PlatformLocation
    ) {}

    public trackPageLoad(id: string): void {
        const interaction = this.getPageAnalyticsById(id);

        if (!interaction) {
            return;
        }

        interaction.pageInfo.title = this.titleService.getTitle();
        interaction.pageInfo.url = window.location.href; // Has to be set here as this changes very late

        this.analyticsService.trackPageLoad(interaction);
    }

    public async trackInteraction(id: string, overrides?: IAdobeInteraction): Promise<void> {
        let interaction: IAdobeInteraction | null = await this.getInteractionById(id);

        if (!interaction) {
            return;
        }

        if (overrides) {
            const combinedResults = ObjectHelpers.mergeObjects(interaction, overrides);
            interaction = combinedResults;
        }

        interaction["link"] = interaction["link"] || {};
        interaction.link.url = this.formatUrl(interaction.link.url || "");

        this.analyticsService.trackInteraction(interaction);
    }

    public trackCustom(interaction: IAdobeInteraction) {
        if (!interaction) {
            return;
        }

        if (!interaction.link) {
            interaction["link"] = {};
        }

        // Format paths
        interaction.link.url = this.formatUrl(interaction.link.url || "");

        this.analyticsService.trackInteraction(interaction);
    }

    public async getInteractionById(id: string): Promise<IAdobeInteraction | null> {
        return new Promise((resolve) => {
            const interactions = this.getAllInteractionAnalytics();

            if (interactions[id]) {
                resolve(ObjectHelpers.mergeObjects({}, interactions[id]));
            }

            resolve(null);
        });
    }

    private getPageAnalyticsById(id: string): IAdobePageLoad | null {
        const pageAnalytics = this.getAllPageLoadAnalytics();

        if (pageAnalytics[id]) {
            return pageAnalytics[id];
        }

        return null;
    }

    private getAllInteractionAnalytics(): InteractionAnalyticsMap {
        if (this._interactionAnalytics) {
            return this._interactionAnalytics;
        } else {
            this._interactionAnalytics = analyticsInteractions;

            return this._interactionAnalytics;
        }
    }

    private getAllPageLoadAnalytics(): PageLoadAnalyticsMap {
        if (this._pageAnalytics) {
            return this._pageAnalytics;
        } else {
            this._pageAnalytics = pageLoadAnalytics;

            return this._pageAnalytics;
        }
    }

    private formatUrl(url: string | ""): string {
        if (url.indexOf("://") > 0 || url.indexOf("//") === 0) {
            // Absolute url
            return url;
        }
        // Build url if provided path or current page url when empty
        else if (url.length) {
            return window.location.origin + this.platformLocation.getBaseHrefFromDOM() + url;
        } else {
            return window.location.href;
        }
    }
}
